import { AlertTriangle, CheckCircle2, Info, Inbox, RefreshCw, WifiOff, XCircle } from 'lucide-react';
import type { ReactNode } from 'react';
import { ApiError } from '@/lib/api';
import { cn } from '@/lib/utils';

export function Spinner({ className, label }: { className?: string; label?: string }) {
  return (
    <svg className={cn('animate-spin', className ?? 'h-5 w-5')} viewBox="0 0 24 24" fill="none" role={label ? 'status' : undefined} aria-label={label} aria-hidden={label ? undefined : true}>
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3" />
      <path d="M22 12a10 10 0 0 0-10-10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

export function Skeleton({ className }: { className?: string }) {
  return <div className={cn('animate-pulse rounded-lg bg-surface-2', className)} aria-hidden="true" />;
}

export function PageLoader({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="grid min-h-[50vh] place-items-center text-muted">
      <Spinner className="h-7 w-7" label={label} />
    </div>
  );
}

type Tone = 'info' | 'success' | 'warning' | 'error';
const toneStyles: Record<Tone, { box: string; icon: ReactNode }> = {
  info: { box: 'border-sky-200 bg-sky-50 text-sky-900', icon: <Info className="h-4 w-4 shrink-0" aria-hidden /> },
  success: { box: 'border-emerald-200 bg-emerald-50 text-emerald-900', icon: <CheckCircle2 className="h-4 w-4 shrink-0" aria-hidden /> },
  warning: { box: 'border-amber-200 bg-amber-50 text-amber-900', icon: <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden /> },
  error: { box: 'border-red-200 bg-red-50 text-red-900', icon: <XCircle className="h-4 w-4 shrink-0" aria-hidden /> },
};

export function Alert({ tone = 'info', title, children, className }: { tone?: Tone; title?: string; children?: ReactNode; className?: string }) {
  const s = toneStyles[tone];
  return (
    <div role={tone === 'error' ? 'alert' : 'status'} className={cn('flex gap-3 rounded-xl border p-3.5 text-[13px] leading-relaxed', s.box, className)}>
      <span className="mt-0.5">{s.icon}</span>
      <div className="min-w-0">
        {title && <p className="font-semibold">{title}</p>}
        {children && <div className={title ? 'mt-0.5' : undefined}>{children}</div>}
      </div>
    </div>
  );
}

export function EmptyState({ title, description, action, icon }: { title: string; description?: string; action?: ReactNode; icon?: ReactNode }) {
  return (
    <div className="rounded-2xl border border-dashed border-line-strong bg-surface/60 px-6 py-12 text-center">
      <div className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-white text-muted">{icon ?? <Inbox className="h-5 w-5" aria-hidden />}</div>
      <h3 className="mt-4 text-[15px] font-bold">{title}</h3>
      {description && <p className="mx-auto mt-1 max-w-md text-[13px] text-muted">{description}</p>}
      {action && <div className="mt-5 flex justify-center">{action}</div>}
    </div>
  );
}

export function ErrorState({ error, onRetry, title }: { error: unknown; onRetry?: () => void; title?: string }) {
  const offline = error instanceof ApiError && error.isNetworkError;
  const message = error instanceof ApiError ? error.message : 'Something went wrong while loading this content.';
  return (
    <div role="alert" className="rounded-2xl border border-red-100 bg-red-50/60 px-6 py-10 text-center">
      <div className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-white text-red-600">
        {offline ? <WifiOff className="h-5 w-5" aria-hidden /> : <AlertTriangle className="h-5 w-5" aria-hidden />}
      </div>
      <h3 className="mt-4 text-[15px] font-bold text-ink">{title ?? (offline ? 'You appear to be offline' : 'Unable to load')}</h3>
      <p className="mx-auto mt-1 max-w-md text-[13px] text-ink-soft">{message}</p>
      {error instanceof ApiError && error.requestId && <p className="mt-2 text-[11px] text-muted">Reference: {error.requestId}</p>}
      {onRetry && (
        <button type="button" onClick={onRetry} className="mt-5 inline-flex items-center gap-2 rounded-full border border-line-strong bg-white px-4 py-2 text-[13px] font-semibold hover:bg-surface">
          <RefreshCw className="h-4 w-4" aria-hidden /> Try again
        </button>
      )}
    </div>
  );
}
